import React, { ReactElement, useMemo } from 'react';
import Select, { Props as SelectProps, GroupBase } from 'react-select';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  buildModernSelectStyles,
  mergeStyles,
  ModernSelectSize,
} from './modernSelectStyles';

export interface ModernSelectOption {
  value: string;
  label: string;
}

export type ModernSelectProps<
  TOption extends ModernSelectOption,
  IsMulti extends boolean = false,
> = SelectProps<TOption, IsMulti, GroupBase<TOption>> & {
  size?: ModernSelectSize;
};

export const ModernSelect = <
  TOption extends ModernSelectOption,
  IsMulti extends boolean = false,
>(
  props: ModernSelectProps<TOption, IsMulti>
): ReactElement => {
  const {
    size = 'compact',
    styles,
    menuPortalTarget,
    menuPosition,
    ...rest
  } = props;

  const { branding } = useBranding();
  const primary: string = branding.colours.primary.regular;

  const mergedStyles = useMemo(() => {
    const baseline = buildModernSelectStyles<TOption, IsMulti>(primary, size);
    return mergeStyles<TOption, IsMulti>(baseline, styles);
  }, [primary, size, styles]);

  return (
    <Select<TOption, IsMulti, GroupBase<TOption>>
      {...rest}
      styles={mergedStyles}
      menuPortalTarget={menuPortalTarget ?? document.body}
      menuPosition={menuPosition ?? 'fixed'}
    />
  );
};

export default ModernSelect;
